import type { Styles } from '@adornis/ass/style.js';
import { BuchungsheldLitElement } from '@adornis/buchungsheld-components/BuchungsheldLitElement.js';
import { acss } from '@adornis/chemistry/directives/acss.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { html } from 'lit';
import { customElement, property } from 'lit/decorators.js';

/**
 * @element bh-signup-preview
 */
@customElement('bh-signup-preview')
export class BhSignupPreview extends BuchungsheldLitElement {
  @property({ attribute: false, type: Boolean }) loading = false;
  @property() mode?: 'institutions' | 'workshops' | 'single' = 'workshops';

  private readonly grayColor = this.colors.neutral.shade(200);
  private readonly bgColor = this.colors.tone.snowWhite!;

  override render() {
    return html`
      <tr-flex
        w100
        flex
        padding="lg"
        ${acss({
          position: 'relative',
          color: this.grayColor.shade(700),
          border: `2px solid ${this.grayColor}`,
          borderRadius: this.sizes.borderRadius,
          marginTop: `calc(2 * ${this.spacing.xs} + 11px)`,
        })}
      >
        ${(
          { institutions: this._institutions, workshops: this._workshops, single: this._single }[this.mode || ''] ??
          this._workshops
        ).bind(this)()}
        ${this._previewIndicator()}
      </tr-flex>
    `;
  }

  private _institutions() {
    return html`
      <tr-flex placeholder-wrapper space="lg" ${acss({ '[cover]': { width: `calc( ${this.spacing.xxl} * 2 )` } })}>
        <tr-flex container horizontal center w100 flex>
          <tr-flex cover></tr-flex>

          <tr-flex content space="md" padding="md" space-between crossaxis-start w100 flex>
            <tr-flex space="md" w100 flex>
              <x-h1> Institution 1 </x-h1>

              <tr-flex space="xs" w100>
                <x-text text> .... </x-text>
                <x-text text> .... </x-text>
                <x-text text> .... </x-text>
                <x-text text> .... </x-text>
              </tr-flex>
            </tr-flex>
          </tr-flex>
        </tr-flex>

        <tr-flex container horizontal center w100 flex>
          <tr-flex content space="md" padding="md" space-between crossaxis-start w100 flex>
            <tr-flex space="md" w100 flex>
              <x-h1 ${css({ alignSelf: 'flex-end' })}> Institution 2 </x-h1>

              <tr-flex space="xs" w100>
                <x-text text> .... </x-text>
                <x-text text> .... </x-text>
                <x-text text> .... </x-text>
                <x-text text> .... </x-text>
              </tr-flex>
            </tr-flex>
          </tr-flex>

          <tr-flex cover></tr-flex>
        </tr-flex>

        <tr-flex overlay></tr-flex>
      </tr-flex>
    `;
  }

  private _workshops() {
    const item = html`
      <tr-flex container horizontal space="md" padding="md" center flex>
        <tr-flex center space="sm">
          <tr-flex cover space="sm" padding="sm" crossaxis-center>
            <tr-flex horizontal space="xs" crossaxis-end w100>
              <tr-text> von </tr-text> <tr-h3 text> 14 Nov</tr-h3>
            </tr-flex>
            <tr-flex horizontal space="xs" crossaxis-end w100>
              <tr-text> bis </tr-text> <tr-h3 text> 14 Nov</tr-h3>
            </tr-flex>
          </tr-flex>

          <tr-button button disabled> BeispielTextLänge </tr-button>
        </tr-flex>

        <tr-flex content space-between crossaxis-start w100 flex>
          <tr-flex space="lg" w100 flex>
            <tr-flex horizontal space="md" flex>
              <x-h1 text> .... </x-h1>
              <tr-flex horizontal space="sm" crossaxis-center>
                <tr-text> Auswählen </tr-text>
                <tr-flex
                  ${acss({
                    minWidth: this.spacing.md,
                    height: this.spacing.md,
                    background: this.grayColor,
                    borderRadius: '50%',
                  })}
                ></tr-flex>
              </tr-flex>
            </tr-flex>

            <tr-flex space="xs" w100>
              <x-text text> .... </x-text>
              <x-text text> .... </x-text>
            </tr-flex>

            <tr-flex horizontal space="xs">
              <tr-chip chip> BeispielTextLänge </tr-chip>
              <tr-chip chip> BeispielTextLänge </tr-chip>
            </tr-flex>
          </tr-flex>
        </tr-flex>
      </tr-flex>
    `;

    return html`
      <tr-flex
        placeholder-wrapper
        space="md"
        ${acss({
          '[cover]': {
            color: this.bgColor,
            background: this.grayColor,
            borderRadius: this.spacing.xs,

            'tr-text': { marginBottom: this.spacing.xxs },
          },
        })}
      >
        ${[item, item]}
      </tr-flex>
    `;
  }

  private _single() {
    return html`
      <tr-flex
        container
        space="xl"
        padding="md"
        crossaxis-start
        flex
        ${acss({ 'tr-h1': { lineHeight: this.spacing.xl } })}
      >
        <tr-flex horizontal space="md" center w100>
          <tr-chip chip> 1 Termine auswählen </tr-chip>
          <tr-chip chip> 2 Persönliche Daten </tr-chip>
          <tr-chip chip> 3 Nutzungsbedingungen </tr-chip>
          <tr-chip chip> 4 Anmeldung abschließen </tr-chip>
        </tr-flex>

        <tr-flex space="md" center>
          <tr-flex space="xs" center>
            <x-text> Ausgewählte Termine </x-text>
            <tr-chip chip>
              <tr-h4> 01- Unternehmensführung in Zeiten von KI </tr-h4>
              <tr-text> am 11/14/2023 von 9:00 AM bis 3:00 PM </tr-text>
            </tr-chip>
          </tr-flex>

          <tr-flex space="xs" center>
            <x-text> Ausgewählte Rabatte </x-text>
            <tr-chip chip>
              <tr-h4> 01- Unternehmensführung in Zeiten von KI </tr-h4>
              <tr-text> am 11/14/2023 von 9:00 AM bis 3:00 PM </tr-text>
            </tr-chip>
          </tr-flex>
        </tr-flex>

        <tr-flex space="sm" center w100>
          <tr-flex space="xxs" center w100>
            <x-text> E-Mail* </x-text>
            <tr-h1 text> - </tr-h1>
          </tr-flex>

          <tr-flex space="xxs" center w100>
            <x-text> Vorname* </x-text>
            <tr-h1 text> - </tr-h1>
          </tr-flex>

          <tr-flex space="xxs" center w100>
            <x-text> Nachname* </x-text>
            <tr-h1 text> - </tr-h1>
          </tr-flex>
        </tr-flex>
      </tr-flex>
    `;
  }

  private _previewIndicator() {
    return html`
      <x-flex
        padding="xs"
        ${css({
          background: this.grayColor,
          borderTopLeftRadius: this.sizes.borderRadius,
          borderTopRightRadius: this.sizes.borderRadius,
          position: 'absolute',
          bottom: '100%',
          right: this.spacing.lg,
          color: this.colors.on.neutral.shade(200),
        })}
      >
        <x-text> Formular Vorschau </x-text>
      </x-flex>
    `;
  }

  override styles() {
    return [
      ...super.styles(),
      {
        ':host': {
          display: 'flex',
          userSelect: 'none',
        },
        '@keyframes sliding': {
          '0%': {
            left: '-100%',
          },
          '100%': {
            left: '100%',
          },
        },
        '[cover]': {
          background: this.grayColor,
        },
        '[text]': {
          width: '100%',
          color: this.grayColor,
          background: this.grayColor,
          borderRadius: this.spacing.xxs,
        },
        '[button]': {
          color: this.grayColor,
          borderColor: this.grayColor,
          background: this.grayColor,
          opacity: '1',
        },
        '[chip]': {
          color: this.grayColor,
          borderColor: this.grayColor,
          background: this.grayColor,
        },

        '[overlay]': this.loading
          ? {
              position: 'absolute',
              top: '0',
              left: '0',
              width: '100%',
              bottom: '0',

              background: `linear-gradient(75deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0) 17%, ${this.bgColor.opacity(
                0.87,
              )} 30%, ${this.bgColor} 37%, ${this.bgColor} 50%, ${this.bgColor.opacity(
                0.33,
              )} 60%, rgba(0,0,0,0) 65%, rgba(0,0,0,0) 100%)`,

              animation: `2s linear 0s sliding infinite`,

              '&[second]': {
                animationDelay: '-1s',
              },
            }
          : { display: 'none' },
      },
    ] as Styles[];
  }
}
